import type { BoxSummary } from "@loot-os/api-contract";

import type { BoxSort, BoxTab } from "./search.js";

/** A box counts as new for this many days after it was added. */
export const NEW_BOX_DAYS = 30;

const DAY_MS = 86_400_000;

/** Whether the box was added in the last `NEW_BOX_DAYS`; a box without a date never is. */
export function isNewBox(box: BoxSummary, now: number): boolean {
  const added = box.createdAt ? Date.parse(box.createdAt) : Number.NaN;
  return !Number.isNaN(added) && now - added <= NEW_BOX_DAYS * DAY_MS;
}

/** What the grid shows: every filter the page offers, resolved to plain values. */
export interface BoxCriteria {
  tab: BoxTab;
  query: string;
  minCents: number | null;
  maxCents: number | null;
  /** What the shopper can spend, when only affordable boxes are asked for; null = no cap. */
  budgetCents: number | null;
  favorites: ReadonlySet<string>;
  sort: BoxSort;
  now: number;
}

/** The boxes the criteria keep, in the order they ask for. */
export function filterBoxes(boxes: readonly BoxSummary[], criteria: BoxCriteria): BoxSummary[] {
  const query = criteria.query.trim().toLowerCase();
  const kept = boxes.filter(
    (box) =>
      (criteria.tab !== "new" || isNewBox(box, criteria.now)) &&
      (criteria.tab !== "favorites" || criteria.favorites.has(box.id)) &&
      (query === "" || box.name.toLowerCase().includes(query)) &&
      (criteria.minCents === null || box.priceCents >= criteria.minCents) &&
      (criteria.maxCents === null || box.priceCents <= criteria.maxCents) &&
      (criteria.budgetCents === null || box.priceCents <= criteria.budgetCents),
  );
  switch (criteria.sort) {
    case "default":
      return kept;
    case "price-asc":
      return kept.sort((a, b) => a.priceCents - b.priceCents);
    case "price-desc":
      return kept.sort((a, b) => b.priceCents - a.priceCents);
    case "popular":
      return kept.sort((a, b) => b.openedCount - a.openedCount);
  }
}
